import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { receiptService } from '../../apiServices/receiptService';
import { accountService } from '../../apiServices';
import { Receipt, TransactionType, ReceiptStatus, Account } from '../../apiServices/types';
import { useNotification } from '../../contexts/NotificationContext';
import { formatCurrency, toUTCDateString } from '../../utils';
import { Modal, ConfirmationModal, GlassSelect } from '../UI';
import { CheckIcon, CalendarIcon } from '../UI/Icons';
import './ReceiptDetailsModal.css';

interface NameEditComponentProps {
  receipt: Receipt;
  onUpdate: () => void;
  onReceiptUpdate?: (updatedReceipt: Receipt) => void;
  disabled?: boolean;
  onEditingChange?: (field: string, isEditing: boolean, hasChanges: boolean) => void;
}

const NameEditComponent: React.FC<NameEditComponentProps> = ({ receipt, onUpdate, onReceiptUpdate, disabled = false, onEditingChange }) => {
  const { showNotification } = useNotification();
  const [isEditing, setIsEditing] = useState(false);
  const [nameValue, setNameValue] = useState(receipt.name);

  React.useEffect(() => {
    if (!isEditing) {
      setNameValue(receipt.name);
    }
  }, [receipt.name, isEditing]);

  React.useEffect(() => {
    if (onEditingChange) {
      const hasChanges = isEditing && nameValue.trim() !== receipt.name.trim();
      onEditingChange('name', isEditing, hasChanges);
    }
  }, [isEditing, nameValue, receipt.name, onEditingChange]);

  // Відстежуємо зміни навіть коли не в режимі редагування, щоб скинути стан
  React.useEffect(() => {
    if (!isEditing && onEditingChange) {
      onEditingChange('name', false, false);
    }
  }, [isEditing, onEditingChange]);

  const handleSave = async () => {
    if (!nameValue.trim()) {
      showNotification('Назва не може бути порожньою', 'error');
      return;
    }

    try {
      const updatedReceipt = await receiptService.update(receipt.id, { name: nameValue.trim(), date: receipt.date });
      setIsEditing(false);
      if (onEditingChange) {
        onEditingChange('name', false, false);
      }
      showNotification('Назву оновлено', 'success');
      onUpdate();
      onReceiptUpdate?.(updatedReceipt);
    } catch (error) {
      showNotification('Помилка оновлення назви', 'error');
    }
  };

  const handleCancel = () => {
    setNameValue(receipt.name);
    setIsEditing(false);
    if (onEditingChange) {
      onEditingChange('name', false, false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <div className="name-edit-inline">
        <input
          type="text"
          value={nameValue}
          onChange={(e) => setNameValue(e.target.value)}
          onKeyDown={handleKeyDown}
          className="form-input name-input-inline"
          autoFocus
        />
        <button className="receipt-edit-btn receipt-edit-btn-save" onClick={handleSave} title="Зберегти">
          <CheckIcon size={18} />
        </button>
        <button className="receipt-edit-btn receipt-edit-btn-cancel" onClick={handleCancel} title="Скасувати">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
    );
  }

  return (
    <div className="receipt-name-display">
      <h3 className="receipt-name">{receipt.name}</h3>
      {!disabled && (
        <button className="btn-icon-edit" onClick={() => setIsEditing(true)} title="Редагувати назву">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
          </svg>
        </button>
      )}
    </div>
  );
};

interface DateEditComponentProps {
  receipt: Receipt;
  onUpdate: () => void;
  onReceiptUpdate?: (updatedReceipt: Receipt) => void;
  disabled?: boolean;
  onEditingChange?: (field: string, isEditing: boolean, hasChanges: boolean) => void;
}

const DateEditComponent: React.FC<DateEditComponentProps> = ({ receipt, onUpdate, onReceiptUpdate, disabled = false, onEditingChange }) => {
  const { showNotification } = useNotification();
  const [isEditing, setIsEditing] = useState(false);
  
  // Функція для отримання дати у форматі YYYY-MM-DD без урахування часу
  const getDateOnly = (dateString: string): string => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const [dateValue, setDateValue] = useState(receipt.date ? getDateOnly(receipt.date) : '');

  // Синхронізуємо dateValue з receipt.date при зміні receipt, але тільки коли не в режимі редагування
  React.useEffect(() => {
    if (receipt.date && !isEditing) {
      setDateValue(getDateOnly(receipt.date));
    }
  }, [receipt.date, isEditing]);

  React.useEffect(() => {
    if (onEditingChange) {
      const originalDate = receipt.date ? getDateOnly(receipt.date) : '';
      const hasChanges = isEditing && dateValue !== originalDate;
      onEditingChange('date', isEditing, hasChanges);
    }
  }, [isEditing, dateValue, receipt.date, onEditingChange]);

  // Відстежуємо зміни навіть коли не в режимі редагування, щоб скинути стан
  React.useEffect(() => {
    if (!isEditing && onEditingChange) {
      onEditingChange('date', false, false);
    }
  }, [isEditing, onEditingChange]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('uk-UA', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleSave = async () => {
    try {
      let dateWithTime: string = dateValue;
      if (dateValue) {
        dateWithTime = toUTCDateString(dateValue);
      }

      const updatedReceipt = await receiptService.update(receipt.id, { name: receipt.name, date: dateWithTime });
      setIsEditing(false);
      showNotification('Дату оновлено', 'success');
      onUpdate();
      onReceiptUpdate?.(updatedReceipt);
    } catch (error) {
      showNotification('Помилка оновлення дати', 'error');
    }
  };

  const handleCancel = () => {
    setDateValue(receipt.date ? getDateOnly(receipt.date) : '');
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <div className="date-edit-inline">
        <div className="date-input-wrapper">
          <input
            type="date"
            value={dateValue}
            onChange={(e) => setDateValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="form-input date-input-custom"
          />
          <label htmlFor="receipt-date" className="date-input-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
          </label>
        </div>
        <button className="receipt-edit-btn receipt-edit-btn-save" onClick={handleSave} title="Зберегти">
          <CheckIcon size={18} />
        </button>
        <button className="receipt-edit-btn receipt-edit-btn-cancel" onClick={handleCancel} title="Скасувати">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
    );
  }

  return (
    <div className="detail-value-date">
      <span>{formatDate(receipt.date)}</span>
      {!disabled && (
        <button className="btn-icon-edit" onClick={() => setIsEditing(true)} title="Редагувати дату">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
          </svg>
        </button>
      )}
    </div>
  );
};

interface AccountEditComponentProps {
  receipt: Receipt;
  onUpdate: () => void;
  onReceiptUpdate?: (updatedReceipt: Receipt) => void;
  disabled?: boolean;
  onEditingChange?: (field: string, isEditing: boolean, hasChanges: boolean) => void;
}

const AccountEditComponent: React.FC<AccountEditComponentProps> = ({ receipt, onUpdate, onReceiptUpdate, disabled = false, onEditingChange }) => {
  const { showNotification } = useNotification();
  const [isEditing, setIsEditing] = useState(false);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [accountValue, setAccountValue] = useState<string>(receipt.accountId || '');

  useEffect(() => {
    const loadAccounts = async () => {
      try {
        const data = await accountService.getAll();
        setAccounts(data);
      } catch (error) {
        showNotification('Помилка завантаження рахунків', 'error');
      }
    };
    loadAccounts();
  }, [showNotification]);

  React.useEffect(() => {
    if (!isEditing) {
      setAccountValue(receipt.accountId || '');
    }
  }, [receipt.accountId, isEditing]);

  React.useEffect(() => {
    if (onEditingChange) {
      const originalAccountId = receipt.accountId || '';
      const hasChanges = isEditing && accountValue !== originalAccountId;
      onEditingChange('account', isEditing, hasChanges);
    }
  }, [isEditing, accountValue, receipt.accountId, onEditingChange]);

  // Відстежуємо зміни навіть коли не в режимі редагування, щоб скинути стан
  React.useEffect(() => {
    if (!isEditing && onEditingChange) {
      onEditingChange('account', false, false);
    }
  }, [isEditing, onEditingChange]);

  const handleSave = async () => {
    try {
      const updatedReceipt = await receiptService.update(receipt.id, { 
        name: receipt.name, 
        date: receipt.date,
        accountId: accountValue || undefined
      });
      setIsEditing(false);
      if (onEditingChange) {
        onEditingChange('account', false, false);
      }
      showNotification('Рахунок оновлено', 'success');
      onUpdate();
      onReceiptUpdate?.(updatedReceipt);
    } catch (error) {
      showNotification('Помилка оновлення рахунку', 'error');
    }
  };

  const handleCancel = () => {
    setAccountValue(receipt.accountId || '');
    setIsEditing(false);
    if (onEditingChange) {
      onEditingChange('account', false, false);
    }
  };

  const getAccountName = (accountId: string | undefined): string => {
    if (!accountId) return 'Не вказано';
    const account = accounts.find(a => a.id === accountId);
    return account ? account.name : 'Не вказано';
  };

  if (isEditing) {
    return (
      <div className="account-edit-inline">
        <div style={{ flex: 1, minWidth: 0 }}>
          <GlassSelect
            options={accounts.map(account => ({
              value: account.id,
              label: account.name
            }))}
            value={accountValue}
            onChange={(value) => setAccountValue(value)}
            placeholder="Оберіть рахунок"
            searchable
          />
        </div>
        <button className="receipt-edit-btn receipt-edit-btn-save" onClick={handleSave} title="Зберегти">
          <CheckIcon size={18} />
        </button>
        <button className="receipt-edit-btn receipt-edit-btn-cancel" onClick={handleCancel} title="Скасувати">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
    );
  }

  return (
    <div className="detail-value-account">
      <span>{getAccountName(receipt.accountId)}</span>
      {!disabled && (
        <button className="btn-icon-edit" onClick={() => setIsEditing(true)} title="Редагувати рахунок">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
          </svg>
        </button>
      )}
    </div>
  );
};

interface ReceiptDetailsModalProps {
  receipt: Receipt;
  onClose: () => void;
  onUpdate: () => void;
  onReceiptUpdate?: (updatedReceipt: Receipt) => void;
}

const ReceiptDetailsModal: React.FC<ReceiptDetailsModalProps> = ({
  receipt,
  onClose,
  onUpdate,
  onReceiptUpdate
}) => {
  const { showNotification } = useNotification();
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [editingStates, setEditingStates] = useState<{ [key: string]: { isEditing: boolean; hasChanges: boolean } }>({});
  const navigate = useNavigate();
  
  const isDeleted = receipt.isDeleted ?? false;

  const handleEditingChange = useCallback((field: string, isEditing: boolean, hasChanges: boolean) => {
    setEditingStates(prev => {
      const newState = {
        ...prev,
        [field]: { isEditing, hasChanges }
      };
      return newState;
    });
  }, []);

  const hasUnsavedChanges = useMemo(() => {
    return Object.values(editingStates).some(state => state.isEditing && state.hasChanges);
  }, [editingStates]);

  const handleClose = useCallback(() => {
    // Перевіряємо чи є незбережені зміни перед закриттям
    const currentHasChanges = Object.values(editingStates).some(state => state.isEditing && state.hasChanges);
    
    if (currentHasChanges) {
      setShowConfirmClose(true);
      return;
    }
    onClose();
  }, [editingStates, onClose]);

  const handleConfirmClose = () => {
    setShowConfirmClose(false);
    setEditingStates({});
    onClose();
  };

  const handleCancelClose = () => {
    setShowConfirmClose(false);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('uk-UA', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toTypeName = (type: unknown): 'Expense' | 'Income' => {
    if (typeof type === 'string') return type === 'Income' ? 'Income' : 'Expense';
    return Number(type) === TransactionType.Income ? 'Income' : 'Expense';
  };

  const isIncomeType = (type: unknown): boolean => toTypeName(type) === 'Income';

  const toStatusName = (status: unknown): 'Completed' | 'InProgress' => {
    if (typeof status === 'string') return status === 'Completed' ? 'Completed' : 'InProgress';
    return Number(status) === ReceiptStatus.Completed ? 'Completed' : 'InProgress';
  };

  const isCompletedStatus = (status: unknown): boolean => toStatusName(status) === 'Completed';

  const getStatusText = (status: unknown) => {
    return isCompletedStatus(status) ? 'Завершено' : 'В процесі';
  };

  const getTypeText = (type: unknown) => {
    return isIncomeType(type) ? 'Дохід' : 'Витрата';
  };

  const getStatusColor = (status: unknown) => {
    return isCompletedStatus(status) ? 'status-completed' : 'status-in-progress';
  };

  const getTypeColor = (type: unknown) => {
    return isIncomeType(type) ? 'type-income' : 'type-expense';
  };

  const handleDelete = (): void => {
    setShowConfirmDelete(true);
  };

  const handleConfirmDelete = async (): Promise<void> => {
    try {
      setIsDeleting(true);
      await receiptService.delete(receipt.id);
      onUpdate();
      onClose();
      if (isDeleted) {
        showNotification('Чек остаточно видалено', 'success');
      } else {
        showNotification('Чек успішно видалено', 'success');
      }
    } catch (error) {
      showNotification('Помилка видалення чека', 'error');
    } finally {
      setIsDeleting(false);
      setShowConfirmDelete(false);
    }
  };
  
  const handleRestore = async (): Promise<void> => {
    try {
      setIsRestoring(true);
      await receiptService.restore(receipt.id);
      onUpdate();
      onClose();
      showNotification('Чек успішно відновлено', 'success');
    } catch (error) {
      showNotification('Помилка відновлення чека', 'error');
    } finally {
      setIsRestoring(false);
    }
  };

  const handleCancelDelete = (): void => {
    setShowConfirmDelete(false);
  };

  const handleSubmit = async () => {
    if (receipt.status === ReceiptStatus.Completed) {
      showNotification('Чек вже завершено', 'warning');
      return;
    }

    try {
      await receiptService.submit(receipt.id);
      onUpdate();
      onClose();
      showNotification('Чек успішно завершено', 'success');
    } catch (error) {
      showNotification('Помилка завершення чека', 'error');
    }
  };

  const handleRepeat = async () => {
    try{
      var receiptResponse = await receiptService.repeat(receipt.id);
      onClose();
      showNotification('Чек успішно повторено', 'success');
      navigate(`/dashboard/expenses/${receiptResponse.id}`);
    }catch(error){
      showNotification('Помилка повторення платежу', 'error');
    }
  };

  const handleEdit = () => {
    onClose();
    navigate(`/dashboard/expenses/${receipt.id}`);
  };

  const groupedPositions = useMemo(() => {
    const map = new Map<string, typeof receipt.positions>();
    receipt.positions.forEach((p) => {
      const key = p.categoryName || 'Без категорії';
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key)!.push(p);
    });
    
    map.forEach((positions) => {
      positions.sort((a, b) => {
        if (a.isBought !== b.isBought) {
          return a.isBought ? 1 : -1;
        }
        const dateA = a.createdDate ? new Date(a.createdDate).getTime() : 0;
        const dateB = b.createdDate ? new Date(b.createdDate).getTime() : 0;
        return dateA - dateB; 
      });
    });
    
    return Array.from(map.entries());
  }, [receipt]);

  return (
    <>
    <Modal
      isOpen={true}
      onClose={handleClose}
      title="Деталі чека"
      size="md"
      footer={(
        <div className="receipt-modal-actions">
          {!isDeleted && (
            <>
              <button 
                className="btn btn-secondary"
                onClick={handleEdit}
              >
                Редагувати
              </button>
              <button 
                className="btn btn-secondary"
                onClick={handleRepeat}
              >
                Повторити платіж
              </button>
              {receipt.status === ReceiptStatus.InProgress && (
                <button 
                  className="btn btn-primary"
                  onClick={handleSubmit}
                >
                  Завершити чек
                </button>
              )}
            </>
          )}

          {isDeleted ? (
            <>
              <button 
                className="btn btn-success"
                onClick={handleRestore}
                disabled={isRestoring}
              >
                {isRestoring ? 'Відновлення...' : 'Відновити чек'}
              </button>
              <button 
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Видалення...' : 'Видалити остаточно'}
              </button>
            </>
          ) : (
        
            <button 
              className="btn btn-danger"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? 'Видалення...' : 'Видалити чек'}
            </button>
          )}

        </div>
      )}
    >
      <div className="receipt-details-content">
          <div className="receipt-info-section">
            <div className="receipt-main-info">
              <NameEditComponent receipt={receipt} onUpdate={onUpdate} onReceiptUpdate={onReceiptUpdate} disabled={isDeleted} onEditingChange={handleEditingChange} />
              <div className="receipt-meta">
                <span className={`status ${getStatusColor(receipt.status)}`}>
                  {getStatusText(receipt.status)}
                </span>
                <span className={`type ${getTypeColor(receipt.type)}`}>
                  {getTypeText(receipt.type)}
                </span>
                {isDeleted && (
                  <span className="status status-deleted" style={{ color: '#ff6b6b' }}>
                    Видалено
                  </span>
                )}
              </div>
            </div>

            <div className="receipt-details">
              <div className="detail-row editable-account-row">
                <span className="detail-label">Рахунок:</span>
                <AccountEditComponent receipt={receipt} onUpdate={onUpdate} onReceiptUpdate={onReceiptUpdate} disabled={isDeleted} onEditingChange={handleEditingChange} />
              </div>
              <div className="detail-row editable-date-row">
                <span className="detail-label">Дата чека:</span>
                <DateEditComponent receipt={receipt} onUpdate={onUpdate} onReceiptUpdate={onReceiptUpdate} disabled={isDeleted} onEditingChange={handleEditingChange} />
              </div>
              <div className="detail-row">
                <span className="detail-label">Загальна сума:</span>
                <span className={`detail-value amount ${getTypeColor(receipt.type)}`}>
                  {isIncomeType(receipt.type) ? '+' : '-'}
                  {formatCurrency(receipt.totalAmount)}
                </span>
              </div>
            </div>
          </div>

          <div className="positions-section">
            {receipt.positions.length === 0 ? (
              <div className="receipt-details-empty-positions">
                <p>Немає позицій в чеку</p>
              </div>
            ) : (
              groupedPositions.map(([categoryName, positions]) => (
                <div key={categoryName} className="receipt-details-category-group">
                  <div className="receipt-details-category-header">
                    <span className="receipt-details-category-name">{categoryName}</span>
                  </div>
                  <div className="receipt-details-positions-list">
                    {positions.map((position) => (
                      <div 
                        key={position.id} 
                        className={`receipt-details-position-item ${position.isDeleted ? 'receipt-details-position-deleted' : ''}`}
                        style={position.isDeleted ? { opacity: 0.6, textDecoration: 'line-through' } : {}}
                      >
                        <div className="receipt-details-position-info">
                          <span className="receipt-details-position-name">{position.name}</span>
                          {position.isDeleted && (
                            <span className="receipt-details-position-deleted-badge">Видалено</span>
                          )}
                        </div>
                        <div className="receipt-details-position-amount">
                          <span className="receipt-details-amount">{formatCurrency(position.amount)}</span>
                          {position.isBought && !position.isDeleted && (
                            <span className="receipt-details-bought-indicator">
                              <CheckIcon size={18} color="var(--success)" />
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
    </Modal>

    <ConfirmationModal
      isOpen={showConfirmDelete}
      title={isDeleted ? "Видалити чек остаточно?" : "Видалити чек?"}
      message={isDeleted 
        ? "Ви впевнені, що хочете остаточно видалити цей чек? Цю дію неможливо скасувати."
        : "Ви впевнені, що хочете видалити цей чек? Ви зможете відновити його з архіву."}
      confirmText={isDeleted ? "Так" : "Так"}
      cancelText="Скасувати"
      onConfirm={handleConfirmDelete}
      onCancel={handleCancelDelete}
      variant="danger"
    />

    <ConfirmationModal
      isOpen={showConfirmClose}
      title="Незбережені зміни"
      message="У вас є незбережені зміни. Ви впевнені, що хочете закрити модальне вікно? Всі незбережені зміни будуть втрачені."
      confirmText="Так"
      cancelText="Скасувати"
      onConfirm={handleConfirmClose}
      onCancel={handleCancelClose}
      variant="warning"
    />
    </>
  );
};

export default ReceiptDetailsModal;
